/**
 * Supabase Auth wrapper for viisi-web.
 *
 * Replaced the Cognito SDK in the 2026-09 move off AWS. Every export keeps its
 * previous name and shape so AuthGate, LoginForm and api.ts are unchanged.
 *
 * The SDK persists access/refresh tokens in localStorage itself, so we never
 * roll our own token storage.
 *
 * Refresh strategy: getIdToken() calls getSession(), which transparently
 * refreshes an expired access token using the stored refresh token.
 * Concurrent callers share a single in-flight refresh promise.
 */

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { navigate } from './router';

function readConfig(): { url: string; anonKey: string } {
    const url = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!url || !anonKey) {
        // Don't throw at module load - let login() surface a clean error so
        // the form still renders and the user sees what's wrong.
        return { url: '', anonKey: '' };
    }
    return { url, anonKey };
}

const cfg = readConfig();
const supabase: SupabaseClient | null = cfg.url && cfg.anonKey
    ? createClient(cfg.url, cfg.anonKey, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: false },
    })
    : null;

const NOT_CONFIGURED =
    'Kirjautumista ei ole määritetty. Aseta VITE_SUPABASE_URL ja VITE_SUPABASE_ANON_KEY.';

export class AuthError extends Error {
    code: string;
    constructor(code: string, message: string) {
        super(message);
        this.name = 'AuthError';
        this.code = code;
    }
}

export class NotAuthorizedError extends AuthError {
    constructor(message = 'Tällä tilillä ei ole pääsyä sovellukseen.') {
        super('NotAuthorizedForApp', message);
        this.name = 'NotAuthorizedError';
    }
}

export interface AuthUser {
    email: string;
    sub: string;
}

type AuthListener = (user: AuthUser | null) => void;
const listeners = new Set<AuthListener>();
let currentUser: AuthUser | null = null;

/**
 * Dev-only preview state - completely stripped from production bundles
 * via `import.meta.env.DEV`. Lets you see the full AppShell on localhost
 * before the real Supabase project is filled in.
 */
let devPreviewUser: AuthUser | null = null;
const DEV_PREVIEW_SUB = 'dev-preview-user';

function notify(user: AuthUser | null): void {
    currentUser = user;
    for (const cb of listeners) {
        try { cb(user); } catch (err) { console.error('[auth] listener error:', err); }
    }
}

export function onAuthChange(cb: AuthListener): () => void {
    listeners.add(cb);
    // Fire immediately with the current state so subscribers don't have to
    // separately call getSession() on mount.
    cb(currentUser);
    return () => { listeners.delete(cb); };
}

function userFromSession(session: Session): AuthUser {
    return {
        email: session.user.email ?? '',
        sub: session.user.id,
    };
}

function sanitizeAuthError(err: { code?: string; message?: string; status?: number }): AuthError {
    const code = err.code ?? 'UnknownError';
    const message = (err.message ?? '').toLowerCase();

    if (code === 'user_already_exists' || message.includes('already registered')) {
        return new AuthError(code, 'Tällä sähköpostilla on jo tunnus. Kirjaudu sisään.');
    }
    if (code === 'weak_password' || message.includes('password should be')) {
        return new AuthError(code, 'Salasanan pitää olla vähintään 6 merkkiä.');
    }
    if (code === 'invalid_credentials' || message.includes('invalid login credentials')) {
        return new AuthError(code, 'Väärä sähköposti tai salasana.');
    }
    if (code === 'email_not_confirmed' || message.includes('not confirmed')) {
        return new AuthError(code, 'Tiliä ei ole vielä vahvistettu.');
    }
    if (code === 'otp_expired' || message.includes('expired') || message.includes('invalid')) {
        return new AuthError(code, 'Koodi on väärä tai vanhentunut. Tarkista koodi tai pyydä uusi.');
    }
    if (code === 'over_request_rate_limit' || code === 'over_email_send_rate_limit' || err.status === 429) {
        return new AuthError(code, 'Liian monta yritystä. Odota hetki ja yritä uudelleen.');
    }
    if (message.includes('fetch') || message.includes('network')) {
        return new AuthError('NetworkError', 'Yhteysvirhe. Tarkista verkkoyhteys ja yritä uudelleen.');
    }
    return new AuthError(code, err.message ?? 'Kirjautuminen epäonnistui. Yritä uudelleen.');
}

/**
 * Authenticate with Supabase and persist the session via the SDK's
 * built-in localStorage strategy.
 */
export async function login(email: string, password: string): Promise<AuthUser> {
    if (!supabase) throw new AuthError('ConfigError', NOT_CONFIGURED);

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw sanitizeAuthError(error);

    const user = userFromSession(data.session);
    notify(user);
    return user;
}

/**
 * The "open the app" link in the shared Confirm signup template is
 * {{ .RedirectTo }}, which each app fills with `emailRedirectTo`. viisi's
 * points at this flag so the link opens straight on the code step. Supabase
 * only honours it because `https://viisi.woofapp.fi/**` is in its Redirect
 * URLs; anything else falls back to woofapp.fi.
 */
const VERIFY_LINK_FLAG = 'vahvista';

function verifyLinkUrl(): string {
    return `${window.location.origin}/?${VERIFY_LINK_FLAG}`;
}

/**
 * True when the page was opened from the sign-up email's link. Clears the
 * flag from the address bar so a reload or a later sign-in starts clean.
 */
export function consumeVerifyLink(): boolean {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(VERIFY_LINK_FLAG)) return false;
    params.delete(VERIFY_LINK_FLAG);
    const query = params.toString();
    history.replaceState(history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    return true;
}

/**
 * Create an account. The shared Supabase project allows sign-up, but the API
 * only admits subs on its allowlist, so a new account is harmless until
 * someone adds it there. Returns 'signed-in' when the project hands back a
 * session straight away, or 'confirm-email' when it wants the address
 * verified first.
 */
export async function signUp(
    email: string,
    password: string,
): Promise<'signed-in' | 'confirm-email' | 'already-registered'> {
    if (!supabase) throw new AuthError('ConfigError', NOT_CONFIGURED);

    const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: verifyLinkUrl() },
    });
    if (error) throw sanitizeAuthError(error);

    if (data.session) {
        notify(userFromSession(data.session));
        return 'signed-in';
    }
    // A confirmed address: Supabase answers 200 with a stand-in user whose
    // identities list is empty and sends no email, so there is no code to
    // wait for. (An unconfirmed address gets a fresh code instead, and keeps
    // its first password.)
    if (data.user?.identities?.length === 0) return 'already-registered';
    return 'confirm-email';
}

/**
 * Confirm a new account with the code from the sign-up email. The Supabase
 * project is shared with woof, whose email templates send `{{ .Token }}` (a
 * numeric code) rather than a link, so the code is the only way to confirm.
 * A successful check returns a session, so the user lands straight in the app.
 */
export async function verifySignupCode(email: string, code: string): Promise<AuthUser> {
    if (!supabase) throw new AuthError('ConfigError', NOT_CONFIGURED);

    const { data, error } = await supabase.auth.verifyOtp({ email, token: code, type: 'signup' });
    if (error) throw sanitizeAuthError(error);
    if (!data.session) throw new AuthError('NoSession', 'Vahvistus onnistui. Kirjaudu nyt sisään.');

    const user = userFromSession(data.session);
    notify(user);
    return user;
}

/** Send a fresh sign-up code to an account that is not confirmed yet. */
export async function resendSignupCode(email: string): Promise<void> {
    if (!supabase) throw new AuthError('ConfigError', NOT_CONFIGURED);

    const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: verifyLinkUrl() },
    });
    if (error) throw sanitizeAuthError(error);
}

/**
 * Sign out, clear cached tokens, and reset the URL to `/`.
 * AuthGate observes the auth-state change via onAuthChange and re-renders to login.
 */
export function logout(): void {
    // Dev preview bypass - skip Supabase teardown.
    if (devPreviewUser) {
        devPreviewUser = null;
        notify(null);
        if (typeof window !== 'undefined' && window.location.pathname !== '/') {
            navigate('/', { replace: true });
        }
        return;
    }

    // Fire and forget: local state clears regardless so a network failure
    // cannot strand the user in a half-logged-in view.
    supabase?.auth.signOut().catch(() => {});
    notify(null);
    // Clear any stale path from the URL bar so a logged-out user doesn't see
    // /workouts/123 (or whatever) hanging around behind the login form.
    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
        navigate('/', { replace: true });
    }
}

/**
 * Dev-only: drop straight into the app without going through Supabase.
 * No-op in production (call site is also gated, this is defense-in-depth).
 *
 * The "user" gets sub = 'dev-preview-user', and getIdToken() returns that
 * string so apiFetch sends `Authorization: Bearer dev-preview-user`. The
 * backend's MOCK_AUTH=true mock verifier treats that string as the sub,
 * so /api/_status passes and the AppShell renders.
 */
export function devPreviewLogin(): void {
    if (!import.meta.env.DEV) return;
    devPreviewUser = {
        sub: DEV_PREVIEW_SUB,
        email: 'dev-preview@localhost',
    };
    notify(devPreviewUser);
}

export function isDevPreview(): boolean {
    return devPreviewUser !== null;
}

/**
 * Returns the current valid session, or null if there is no signed-in
 * user / the refresh token has expired. Triggers SDK auto-refresh of
 * the access token if needed.
 */
export async function getSession(): Promise<Session | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session) return null;
    return data.session;
}

// Dedup concurrent token refreshes - multiple in-flight API calls hitting
// an expired token shouldn't each trigger their own refresh.
let inflightToken: Promise<string | null> | null = null;

/**
 * Get the current access token, auto-refreshing if expired. Returns null
 * if there's no valid session at all (caller should treat as logged out).
 */
export function getIdToken(): Promise<string | null> {
    // Dev preview bypass - return the fake sub as the bearer. The backend's
    // mock verifier (MOCK_AUTH=true) accepts any string and uses it as the
    // user's sub. This branch dies in production builds.
    if (import.meta.env.DEV && devPreviewUser) {
        return Promise.resolve(devPreviewUser.sub);
    }

    if (inflightToken) return inflightToken;

    inflightToken = (async () => {
        const session = await getSession();
        if (!session) {
            // The session is gone - make sure listeners know.
            if (currentUser) notify(null);
            return null;
        }
        return session.access_token;
    })();

    try {
        return inflightToken;
    } finally {
        // Release the dedup slot on next tick so simultaneous callers share
        // this promise but a later call gets a fresh check.
        Promise.resolve().then(() => { inflightToken = null; });
    }
}

/**
 * Bootstrap the in-memory auth state from any persisted session.
 * Call once at app start (AuthGate does this on mount).
 */
export async function bootstrap(): Promise<AuthUser | null> {
    const session = await getSession();
    if (!session) {
        notify(null);
        return null;
    }
    const user = userFromSession(session);
    notify(user);
    return user;
}

export function getCurrentUser(): AuthUser | null {
    return currentUser;
}
